import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  deletePatientsPatientIdPharmacyOrdersOrderIdLabelsLabelThirdPartyKey,
  getPatientsPatientIdPharmacyOrdersOrderIdLabelsLabelThirdPartyKey,
  postPatientsPatientIdPharmacyOrdersOrderIdShipments,
  getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey,
} from 'api/generated/pharmacy-fulfillment/pharmacy-fulfillment'
import { getGetPharmacyOrdersQueryKey } from 'api/generated/pharmacy-order/pharmacy-order'
import { showToast } from '@quickmd-llc/design-system'
import type { CreateShippingLabelDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { getFulfillmentErrorMessage } from './fulfillmentErrors'

type MutationIds = { patientId: string; orderId: string; labelThirdPartyKey: string }

export function useLabelActions(
  patientId: string,
  orderId: string,
  labelThirdPartyKey: string,
  callbacks?: {
    onVoidSettled?: () => void
    onReprintSuccess?: (label: CreateShippingLabelDoctorApiOutContract) => void
    onConfirmSettled?: () => void
  },
) {
  const queryClient = useQueryClient()

  const reprintMutation = useMutation({
    mutationFn: ({ patientId: pid, orderId: oid, labelThirdPartyKey: key }: MutationIds) =>
      getPatientsPatientIdPharmacyOrdersOrderIdLabelsLabelThirdPartyKey(pid, oid, key),
    onSuccess: (data) => {
      callbacks?.onReprintSuccess?.(data)
    },
    onError: (error: unknown) => {
      showToast({
        type: 'error',
        message: getFulfillmentErrorMessage(error, 'Could not reprint the label.'),
      })
    },
  })

  const voidMutation = useMutation({
    mutationFn: ({ patientId: pid, orderId: oid, labelThirdPartyKey: key }: MutationIds) =>
      deletePatientsPatientIdPharmacyOrdersOrderIdLabelsLabelThirdPartyKey(pid, oid, key),
    onSuccess: (_data, { patientId: pid, orderId: oid }) => {
      showToast({ type: 'success', message: 'Label voided successfully.' })
      void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey(pid, oid),
      })
    },
    onError: (error: unknown, { patientId: pid, orderId: oid }) => {
      showToast({
        type: 'error',
        message: getFulfillmentErrorMessage(error, 'Could not void the label.'),
      })
      void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey(pid, oid),
      })
    },
    onSettled: () => {
      callbacks?.onVoidSettled?.()
    },
  })

  const confirmMutation = useMutation({
    mutationFn: ({ patientId: pid, orderId: oid, labelThirdPartyKey: key }: MutationIds) =>
      postPatientsPatientIdPharmacyOrdersOrderIdShipments(pid, oid, { LabelThirdPartyKey: key }),
    onSuccess: (_data, { patientId: pid, orderId: oid }) => {
      showToast({ type: 'success', message: 'Shipment confirmed.' })
      void queryClient.invalidateQueries({ queryKey: getGetPharmacyOrdersQueryKey() })
      void queryClient.invalidateQueries({
        queryKey: getGetPatientsPatientIdPharmacyOrdersOrderIdQueryKey(pid, oid),
      })
    },
    onError: (error: unknown) => {
      showToast({
        type: 'error',
        message: getFulfillmentErrorMessage(error, 'Could not confirm the shipment.'),
      })
    },
    onSettled: () => {
      callbacks?.onConfirmSettled?.()
    },
  })

  return {
    reprint: () => reprintMutation.mutate({ patientId, orderId, labelThirdPartyKey }),
    isReprinting: reprintMutation.isPending,
    voidLabel: () => voidMutation.mutate({ patientId, orderId, labelThirdPartyKey }),
    isVoiding: voidMutation.isPending,
    confirmShipment: () => confirmMutation.mutate({ patientId, orderId, labelThirdPartyKey }),
    isConfirmingShipment: confirmMutation.isPending,
  }
}
